import { useEffect, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { articles, categories, Article } from "@/mocks/articles";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [activeSlug, setActiveSlug] = useState(slug || "news");
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeSlug, location.pathname]);

  useEffect(() => {
    setFilteredArticles(
      articles.filter((a) => a.categorySlug === activeSlug)
    );
  }, [activeSlug]);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${currentCategory?.name || "内容"} - 佐佐木希官方粉丝资讯站`,
            description: `佐佐木希${currentCategory?.name || ""}相关内容合集`,
            url: `https://sazakigallery.github.io/category/${activeSlug}`,
            isPartOf: {
              "@type": "WebSite",
              name: "佐佐木希官方粉丝资讯站",
              url: "https://sazakigallery.github.io/",
            },
          }),
        }}
      />
      <Navbar />
      <main className="min-h-screen pt-20 bg-background-50">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6">
          <div className="py-4">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: currentCategory?.name || "分类" },
              ]}
            />
          </div>

          <div className="py-8">
            <h1
              className="text-3xl md:text-4xl font-bold text-foreground-950 mb-2"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {currentCategory?.name || "内容分类"}
            </h1>
            <p className="text-foreground-600 text-sm">
              浏览佐佐木希{currentCategory?.name || ""}相关内容
            </p>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-4 -mx-4 px-4 md:mx-0 md:px-0">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200/70"
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="py-8">
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-50 rounded-lg border border-background-200/70 hover:border-background-300 overflow-hidden transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative overflow-hidden aspect-[16/10]">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        title={article.title}
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2.5 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-medium whitespace-nowrap">
                          {article.category}
                        </span>
                      </div>
                      <h2 className="text-base font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span>{article.date}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line text-xs"></i>
                          {article.viewCount.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">暂无相关内容</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}